import { DeleteIcon } from "@chakra-ui/icons";
import { Box, Center, Heading, Link, Text, useToast } from "@chakra-ui/react";
import { FC, useContext } from "react";
import { NavLink } from "react-router";

import { RanaTable } from "@nens/ranalib";
import { Simulation } from "@nens/threedi/openapi-client";

import LiveSimulationsSvg from "@/assets/live_simulations.svg?url";

import { LIVE_SITE, getSimulationsApi, getWebsocketUrl } from "../../apiConfiguration";
import { APP_BASE_HREF } from "../../App";
import { ExplainSideColumn } from "../../components/ExplainSideColumn";
import { Toast } from "../../components/Toast";
import { useWebsocketReadonly } from "../../hooks/websockets";
import { OrganisationContext } from "../../providers/OrganisationProvider";
import { queryClient } from "../../queries/queryClient";
import { useSimulationStatuses, useSimulations } from "../../queries/simulations";

export interface ActiveSimulation {
  id: string | number;
  uid: string;
  name: string;
  date_created: string;
  user_name: string;
  organisation_name: string;
  simulation_slug: string;
  progress: number;
  status: string;
  duration: number;
}

interface ActiveSimulationExtraFields extends ActiveSimulation {
  uuid: string;
  name: string;
  created: string;
  durationHumanized: string;
}

interface ActiveSimulationsState {
  [simulationUid: string]: ActiveSimulation;
}

interface ActiveSimulationMessage {
  type: "active-simulation" | "active-simulations";
  data: { [simulationUid: string]: string };
}

interface StatusMessage {
  type: "status";
  data: {
    status: string;
    simulation_id: number;
  };
}

type ReceivedMessage = ActiveSimulationMessage | StatusMessage;

const activeSimulationsReducer = (state: ActiveSimulationsState, message: ReceivedMessage) => {
  switch (message.type) {
    case "active-simulation": {
      const newState: typeof state = { ...state };
      for (const uid of Object.keys(message.data)) {
        newState[uid] = JSON.parse(message.data[uid] as string) as ActiveSimulation;
      }
      return newState;
    }
    case "active-simulations": {
      const newState: typeof state = {};
      for (const uid of Object.keys(message.data)) {
        newState[uid] = JSON.parse(message.data[uid] as string) as ActiveSimulation;
      }
      return newState;
    }
    case "status": {
      const uid = String(message.data.simulation_id);
      if (state[uid]) {
        return {
          ...state,
          [uid]: { ...state[uid], status: message.data.status },
        };
      }
    }
  }
  return state;
};

const simulationsApi = getSimulationsApi();

const SimulationsLive: FC = () => {
  const { organisation } = useContext(OrganisationContext);

  let [activeSimulations] = useWebsocketReadonly(
    getWebsocketUrl("active-simulations/"),
    {} as ActiveSimulationsState,
    activeSimulationsReducer
  );
  activeSimulations = Object.keys(activeSimulations).reduce((acc: ActiveSimulationsState, val) => {
    if (activeSimulations[val].organisation_name === organisation.name)
      acc[val] = activeSimulations[val];
    return acc;
  }, {});
  const simulationsSatusesResponse = useSimulationStatuses("queued", 5000);
  const simulationStatuses = simulationsSatusesResponse.isSuccess
    ? simulationsSatusesResponse.data.results
    : [];
  const simulationStatusesIds = simulationStatuses.map((item) => parseInt(item.simulationId + ""));

  const simulationIds = Object.keys(activeSimulations).map((key) => parseInt(key));
  const simulationsResponse = useSimulations(simulationIds.concat(simulationStatusesIds));

  const simulationsById: { [id: string]: Simulation } = {};
  simulationsResponse.forEach((response) => {
    if (response.isSuccess) {
      const simulation = response.data as Simulation;
      simulationsById[String(simulation.id)] = simulation;
    }
  });

  // Helper function to get simulation by ID with fallback
  const getSimulationById = (id: string | number): Simulation | undefined => {
    return simulationsById[String(id)];
  };

  const columnDefinitions = [
    {
      title: "Simulation",
      width: "table_cell",
    },
    {
      title: "User",
      width: "table_cell_xs",
    },
    {
      title: "Organisation",
      width: "table_cell_xs",
    },
    {
      title: "Created",
      width: "table_cell_xs",
    },
    {
      title: "Duration",
      width: "table_cell_xs",
    },
  ];

  const renderSimulationLink = (row: ActiveSimulationExtraFields, isRunning: boolean) =>
    isRunning ? (
      <Link href={`https://${LIVE_SITE}/quickstart/simulation/${row.id}/`} isExternal>
        {row.name}
      </Link>
    ) : (
      <NavLink to={`${APP_BASE_HREF}/simulations/${row.id}`}>{row.name}</NavLink>
    );

  const renderDuration = (row: ActiveSimulationExtraFields) =>
    row.duration === 3153600000 ? "Rana Live" : row.durationHumanized || `${row.duration}s`;

  // Preprocess data arrays for the tables
  const queuedSimulationsData = simulationStatuses.map((statusRecord) => {
    const simulation = getSimulationById(statusRecord.simulationId || "");
    return {
      id: simulation?.id || "",
      uuid: simulation?.uuid || "",
      uid: String(simulation?.uuid || ""),
      name: simulation?.name || "",
      created: simulation?.created || "",
      date_created: simulation?.created || "",
      duration: simulation?.duration || 0,
      durationHumanized: simulation?.durationHumanized || "",
      user: simulation?.user || "",
      user_name: simulation?.user || "",
      organisation: simulation?.organisationName || "",
      organisation_name: simulation?.organisationName || "",
      simulation_slug: simulation?.slug || "",
      progress: -9999,
      status: statusRecord.name,
    } as ActiveSimulationExtraFields;
  });

  const statusRunning = ["initialized"];

  const sortOnCreated = (a: ActiveSimulation, b: ActiveSimulation) => {
    const timeA = new Date(a.date_created).getTime();
    const timeB = new Date(b.date_created).getTime();

    if (timeA < timeB) return -1;
    if (timeA > timeB) return 1;
    return 0;
  };

  const running = Object.values(activeSimulations).filter((activeSimulation) =>
    statusRunning.includes(activeSimulation.status)
  );
  const notRunning = Object.values(activeSimulations).filter(
    (activeSimulation) => !statusRunning.includes(activeSimulation.status)
  );
  running.sort(sortOnCreated);
  notRunning.sort(sortOnCreated);

  const runningSimulationsData = running.map((activeSimulation) => {
    const simulation = getSimulationById(activeSimulation.uid);
    return {
      ...activeSimulation,
      id: simulation?.id || "",
      uuid: String(activeSimulation.uid),
      name: simulation?.name || "",
      created: simulation?.created || "",
      durationHumanized: simulation?.durationHumanized || "",
    } as ActiveSimulationExtraFields;
  });

  const finishedSimulationsData = notRunning.map((activeSimulation) => {
    const simulation = getSimulationById(activeSimulation.uid);
    return {
      ...activeSimulation,
      id: simulation?.id || "",
      uuid: String(activeSimulation.uid),
      name: simulation?.name || "",
      created: simulation?.created || "",
      durationHumanized: simulation?.durationHumanized || "",
    } as ActiveSimulationExtraFields;
  });

  const toast = useToast();
  const showToast = (title: string, status: "success" | "error") => {
    toast({
      render: ({ onClose }) => <Toast {...{ title, status, onClose }} />,
      duration: 9000,
      isClosable: true,
    });
  };

  const dequeueOrShutdownSimulation = async (
    simulation: ActiveSimulation,
    action: "dequeue" | "shutdown"
  ) => {
    const pk = Number(simulation.id);
    if (!pk || isNaN(pk)) return;

    try {
      await simulationsApi.simulationsActionsCreate({
        simulationPk: pk,
        data: { name: action },
      });

      showToast("Simulation data saved", "success");
      queryClient.refetchQueries({ queryKey: ["simulations"] });
    } catch (error) {
      showToast("Something went wrong while saving the simulation", "error");
      console.log(error);
    }
  };

  const renderTable = (
    title: string,
    data: ActiveSimulationExtraFields[],
    emptyMessage: string,
    rowAction?: {
      label: string;
      action: (row: ActiveSimulationExtraFields) => void;
      icon: JSX.Element;
    },
    isRunning = false
  ) => (
    <>
      <Heading size="md" mt={18} fontSize={24}>
        {title}
      </Heading>
      <RanaTable
        marginTop="18px"
        height="auto"
        columnDefinitions={columnDefinitions}
        tableDataIds={data.map((row) => row.uuid)}
        emptyElement={
          <tr>
            <td colSpan={columnDefinitions.length}>
              <Center mt={24}>
                <Text color="brand.clear_creek">{emptyMessage}</Text>
              </Center>
            </td>
          </tr>
        }
        hasRowActions={!!rowAction}
        sx={{
          // vertical alignment for the icons and checkboxes
          "& p": {
            marginBottom: 0,
          },
          "& label": {
            marginBottom: 0,
          },
        }}
      >
        <RanaTable.Container>
          <RanaTable.Header />
          <RanaTable.Body>
            {data.map((row) => (
              <RanaTable.Row
                rowId={row.uuid}
                key={row.uuid}
                {...(rowAction && {
                  rowActions: [
                    {
                      label: rowAction.label,
                      action: () => rowAction.action(row),
                      icon: rowAction.icon,
                    },
                  ],
                })}
              >
                <RanaTable.Cell>{renderSimulationLink(row, isRunning)}</RanaTable.Cell>
                <RanaTable.Cell>{row.user_name}</RanaTable.Cell>
                <RanaTable.Cell>{row.organisation_name}</RanaTable.Cell>
                <RanaTable.Cell>{row.created}</RanaTable.Cell>
                <RanaTable.Cell>{renderDuration(row)}</RanaTable.Cell>
              </RanaTable.Row>
            ))}
          </RanaTable.Body>
        </RanaTable.Container>
      </RanaTable>
    </>
  );

  return (
    <ExplainSideColumn
      imgUrl={LiveSimulationsSvg}
      imgAltDescription={"Live simulations"}
      headerText={"Live Simulations"}
      explanationText={
        <>
          <p>
            <Box as="strong" textTransform="uppercase" color="brand.analytic">
              Currently running simulations
            </Box>
          </p>
          <p>
            Click on a running simulation to view it in Rana Live. Click on a finished simulation to
            see its archive page.
          </p>
        </>
      }
      backUrl={"/"}
    >
      <Box mt={-2}>
        {renderTable(
          "Queued simulations",
          queuedSimulationsData,
          "There are no queued simulations",
          {
            label: "Dequeue simulation",
            action: (row) => dequeueOrShutdownSimulation(row, "dequeue"),
            icon: <DeleteIcon />,
          }
        )}

        {renderTable(
          "Currently running simulations",
          runningSimulationsData,
          "There are no running simulations",
          {
            label: "Stop simulation",
            action: (row) => dequeueOrShutdownSimulation(row, "shutdown"),
            icon: <DeleteIcon />,
          },
          true
        )}

        {renderTable(
          "Finished while viewing this page",
          finishedSimulationsData,
          "There are no finished simulations"
        )}
      </Box>
    </ExplainSideColumn>
  );
};

export default SimulationsLive;
